import cx from 'classnames';
import { DateTime, Duration } from 'luxon';
import { Button } from '@vimily/bonjoro-ui-components';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import useThumbnailRenderer from '~/App/Components/Record/use-thumbnail-renderer';
import Thumbnail, {
  ThumbnailArgs,
} from '~/App/Components/Record/Stores/Recorder/Thumbnail';
import { SpinnerNew } from '~/App/Components/Shared/Spinner';

export interface ThumbnailPickerProps {
  videoUrl: string;
  onCancel: () => void;
  onSave: (thumbnail: ThumbnailArgs) => Promise<void>;
  saving: boolean;
}

const thumbnailCount = 9;

export default function ThumbnailPicker({
  videoUrl,
  onCancel,
  onSave,
  saving,
}: ThumbnailPickerProps) {
  const videoRef = useRef<HTMLVideoElement>();
  const [selectedThumbnail, setSelectedThumbnail] = useState<ThumbnailArgs>(
    {} as ThumbnailArgs
  );

  const [generatedThumbnails, setGeneratedThumbnails] = useState<Thumbnail[]>(
    []
  );
  const [isReadyToGenerate, setIsReadyToGenerate] = useState(false);
  const [isGeneratingThumb, setIsGeneratingThumb] = useState(true);
  const [thumbnailInterval, setThumbnailInterval] = useState(0);

  const onCompleteCallback = (
    square: string,
    small: string,
    original: string,
    time: DateTime | Duration
  ) => {
    const thumbnail = new Thumbnail({ square, small, original, time });
    setGeneratedThumbnails((thumbnails) => [...thumbnails, thumbnail]);
  };

  const { renderThumbnail } = useThumbnailRenderer({
    videoRef: videoRef,
    onComplete: onCompleteCallback,
    renderImmediately: true,
  });

  const onSeekedHandler = () => {
    if (!videoRef.current || !isReadyToGenerate) {
      return;
    }

    if (generatedThumbnails.length >= thumbnailCount) {
      setIsGeneratingThumb(false);
      return;
    }

    renderThumbnail();
    videoRef.current.currentTime =
      videoRef.current.currentTime + thumbnailInterval;
  };

  const onLoadedHandler = useCallback(() => {
    if (!videoRef.current) return;
    if (!isFinite(videoRef.current.duration)) {
      // fix infinity duration by setting it to a larger number than the duration
      videoRef.current.currentTime = Number.MAX_SAFE_INTEGER;
    } else {
      setIsReadyToGenerate(true);
    }
  }, [videoRef]);

  const onTimeUpdateHandler = useCallback(() => {
    if (
      !isReadyToGenerate &&
      videoRef.current &&
      isFinite(videoRef.current.duration)
    ) {
      setIsReadyToGenerate(true);
      return;
    }
  }, [videoRef.current?.duration, isReadyToGenerate]);

  useEffect(() => {
    if (!videoRef.current || !isReadyToGenerate) {
      return;
    }
    // We are ready to generate
    // reset the currentime in case the video duration was initially infinity
    // and we've set it to a higher number
    videoRef.current.currentTime = 0;
    setThumbnailInterval(videoRef.current.duration / thumbnailCount);
  }, [isReadyToGenerate]);

  return (
    <div className='relative h-full'>
      <div className='text-center text-xl text-neutral-700 font-bold mb-4'>
        Choose a thumbnail
      </div>
      {isGeneratingThumb && (
        <div className='absolute w-full translate-y-60 text-center text-lg text-neutral-700 font-bold z-50'>
          <SpinnerNew /> Generating thumbnails...
        </div>
      )}
      <video
        crossOrigin='crossOrigin'
        src={videoUrl}
        ref={videoRef}
        onLoadedMetadata={onLoadedHandler}
        onSeeked={onSeekedHandler}
        onTimeUpdate={onTimeUpdateHandler}
        className='hidden'
      />
      <div
        className={cx(
          'grid place-items-center sm:grid-cols-2 sm:gap-1 lg:grid-cols-3 h-[565px] p-3 overflow-y-auto mb-4',
          {
            'opacity-50': isGeneratingThumb,
          }
        )}
      >
        {generatedThumbnails.map((thumbnail) => (
          <img
            key={thumbnail.time.toMillis()}
            src={thumbnail.original}
            className={cx(
              'object-contain bg-neutral-700 w-[260px] h-[160px] cursor-pointer transition ease-in-out hover:-translate-y-.5 hover:scale-105 duration-300',
              {
                'border-4 border-turquoise-700 border-solid active':
                  selectedThumbnail.time == thumbnail.time,
              }
            )}
            onClick={() => setSelectedThumbnail(thumbnail)}
          />
        ))}
      </div>
      <div className='text-right'>
        <Button
          variant='info'
          onClick={onCancel}
          className='w-[120px] h-[40px] mr-4'
          disabled={isGeneratingThumb || saving}
        >
          Cancel
        </Button>
        <Button
          variant='default'
          className='w-[120px] h-[40px]'
          onClick={() => onSave(selectedThumbnail)}
          disabled={
            isGeneratingThumb ||
            saving ||
            Object.keys(selectedThumbnail).length === 0
          }
        >
          {saving && <SpinnerNew className='mr-1' />}
          Save
        </Button>
      </div>
    </div>
  );
}
